import supabase from '../supabaseClient'
import { uploadPublicImageWithThumb } from './imageThumbUpload'
import { TABLE_NAMES } from '../config'
import { nameFor } from '../components/signup/countries'
import {
  readDraft,
  readSignupCommunityLogoDataUrl,
  writeDraft,
} from '../components/signup/signupState'
import { migrateCommunityIsPrivate } from './communityPrivacy'
import { gameDefaultsFromWizardPolicies } from './configStyles'
import { persistCommunityContext } from '../utils/communityAccessCache'
import { generateJoinCode } from '../utils/joinCode'
import { persistWizardVenuesToCommunityLocations } from './persistCommunityLocations'
import {
  ensureInitialRatings,
  tryWriteSignupExtras,
} from './signupUserPersist'

const LOGO_BUCKET = 'community_logos'

const SPORT_LABELS = {
  football: 'Football',
  basketball: 'Basketball',
  padel: 'Padel',
}

const PLAYERS_NUMBER_LABELS = {
  under_200: 'Under 200',
  '200_500': '200-500',
  '500_1000': '500-1000',
  '1000_plus': '1000+',
}

function sportLabel(sportId) {
  return SPORT_LABELS[sportId] || 'Football'
}

function playersNumberLabel(communitySizeId) {
  if (!communitySizeId || typeof communitySizeId !== 'string') return null
  return PLAYERS_NUMBER_LABELS[communitySizeId] || null
}

async function uploadLogoDataUrl(communityId, dataUrl) {
  if (!dataUrl?.startsWith('data:')) return null

  const res = await fetch(dataUrl)
  const blob = await res.blob()
  const ext =
    (blob.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '') || 'png'
  const path = `${communityId}/${Date.now()}.${ext}`

  const { publicUrl, thumbPublicUrl } = await uploadPublicImageWithThumb(supabase, {
    bucket: LOGO_BUCKET,
    path,
    file: blob,
    contentType: blob.type || 'image/png',
  })
  return { publicUrl, thumbPublicUrl }
}

/**
 * Columns added after initial launch. Written in a separate update so
 * signup still succeeds when the migration has not been applied yet.
 */
async function tryWriteOptionalCommunityColumns(communityId, fields) {
  const payload = Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value != null && value !== ''),
  )
  if (!communityId || Object.keys(payload).length === 0) return

  const { error } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .update(payload)
    .eq('id', communityId)

  if (error) {
    console.warn(
      'Optional community columns not saved (run migrations for is_private / players_number if needed):',
      error.message,
    )
  }
}

/**
 * Creates the manager's Users row and communities row from the signup
 * draft. Called when the setup wizard finishes (policies step).
 */
/**
 * Ensures a setup-mode community row exists as soon as intake is done,
 * so wizard step 2 can share a real /join/{communityId} link.
 */
export async function ensureSignupCommunityFromIntake() {
  const draft = readDraft()
  if (draft.communityId) {
    return {
      ok: true,
      communityId: draft.communityId,
      communityIdJoin: draft.communityIdJoin || null,
    }
  }

  if (!draft.organizationName?.trim()) {
    return { ok: false, error: 'Community name is required.' }
  }

  const result = await persistSignupCommunity({
    organizationName: draft.organizationName,
    countryIso2: draft.country,
    primarySport: draft.primarySport,
    logoDataUrl: readSignupCommunityLogoDataUrl(),
    fullName: draft.fullName,
    email: draft.email,
    communitySize: '',
    isPrivate: false,
    wizardPolicies: {},
  })

  if (result.ok) {
    writeDraft({
      communityId: result.communityId,
      communityIdJoin: result.communityIdJoin,
      accountStatus: 'activated',
    })
  }

  return result
}

export async function persistSignupCommunityFromDraft() {
  const draft = readDraft()
  return persistSignupCommunity({
    organizationName: draft.organizationName,
    countryIso2: draft.country,
    primarySport: draft.primarySport,
    logoDataUrl: readSignupCommunityLogoDataUrl(),
    fullName: draft.fullName,
    email: draft.email,
    communitySize: draft.setupWizard?.communitySize || '',
    isPrivate: migrateCommunityIsPrivate(draft.setupWizard?.policies || {}),
    wizardPolicies: draft.setupWizard?.policies || {},
  })
}

/**
 * Creates or updates the manager's Users row and communities row from
 * the /signup/community intake form.
 */
export async function persistSignupCommunity({
  organizationName,
  countryIso2,
  primarySport,
  logoDataUrl,
  fullName,
  email,
  communitySize = '',
  isPrivate = false,
  wizardPolicies = {},
}) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser()

  if (userError || !user) {
    return {
      ok: false,
      error: 'You are not signed in. Please sign in again.',
      redirectTo: '/signup/account',
    }
  }

  const trimmedName = organizationName.trim()
  const countryName = nameFor(countryIso2) || countryIso2 || null
  const sport = sportLabel(primarySport)
  const primarySports = primarySport ? [primarySport] : []
  const displayName =
    (fullName || '').trim() ||
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    ''
  const userEmail = (email || user.email || '').trim()

  const { data: existing, error: fetchError } = await supabase
    .from(TABLE_NAMES.USERS)
    .select('user_id, community_id, name, email')
    .eq('user_id', user.id)
    .maybeSingle()

  if (fetchError) {
    return {
      ok: false,
      error: fetchError.message || 'Could not load your profile.',
    }
  }

  let communityId = null
  let communityIdJoin = null

  if (existing?.community_id) {
    const draft = readDraft()
    if (draft.communityId && draft.communityId === existing.community_id) {
      communityId = existing.community_id
    } else {
      const { data: existingComm } = await supabase
        .from(TABLE_NAMES.COMMUNITIES)
        .select('name')
        .eq('id', existing.community_id)
        .maybeSingle()

      return {
        ok: false,
        redirectTo: '/community/login',
        alreadyInCommunity: existingComm?.name || 'your community',
      }
    }
  }

  const communityFields = {
    name: trimmedName,
    sport,
    region: countryName,
    setup_mode: false,
    game_defaults: gameDefaultsFromWizardPolicies(wizardPolicies, trimmedName),
  }

  const optionalCommunityColumns = {
    is_private: migrateCommunityIsPrivate({ ...wizardPolicies, isPrivate }),
  }

  const playersNumber = playersNumberLabel(communitySize)
  if (playersNumber) {
    optionalCommunityColumns.players_number = playersNumber
  }

  if (communityId) {
    const { data: existingComm, error: commFetchError } = await supabase
      .from(TABLE_NAMES.COMMUNITIES)
      .select('community_id_join')
      .eq('id', communityId)
      .maybeSingle()

    if (commFetchError) {
      return {
        ok: false,
        error: commFetchError.message || 'Could not load your community.',
      }
    }

    communityIdJoin = existingComm?.community_id_join || null

    const { error: commUpdateError } = await supabase
      .from(TABLE_NAMES.COMMUNITIES)
      .update(communityFields)
      .eq('id', communityId)

    if (commUpdateError) {
      return {
        ok: false,
        error: commUpdateError.message || 'Could not update your community.',
      }
    }
  } else {
    communityIdJoin = generateJoinCode()
    const { data: communityData, error: communityError } = await supabase
      .from(TABLE_NAMES.COMMUNITIES)
      .insert({
        ...communityFields,
        created_by: user.id,
        community_id_join: communityIdJoin,
      })
      .select('id, community_id_join')
      .single()

    if (communityError) {
      return {
        ok: false,
        error: communityError.message || 'Could not create your community.',
      }
    }

    communityId = communityData.id
    communityIdJoin = communityData.community_id_join || communityIdJoin
  }

  await tryWriteOptionalCommunityColumns(communityId, optionalCommunityColumns)

  if (logoDataUrl) {
    try {
      const uploaded = await uploadLogoDataUrl(communityId, logoDataUrl)
      const logoUrl = uploaded?.publicUrl
      if (logoUrl) {
        const { error: logoUpdateError } = await supabase
          .from(TABLE_NAMES.COMMUNITIES)
          .update({
            logo_url: logoUrl,
            logo_thumb_url: uploaded.thumbPublicUrl || null,
          })
          .eq('id', communityId)
        if (logoUpdateError) {
          console.error('Community logo URL save failed', logoUpdateError)
        }
      }
    } catch (logoErr) {
      console.error('Community logo upload failed', logoErr)
    }
  }

  const userPayload = {
    email: userEmail || null,
    name: displayName || null,
    community_id: communityId,
    community_manager: true,
    country: countryName,
  }

  if (existing) {
    const { error: updateError } = await supabase
      .from(TABLE_NAMES.USERS)
      .update(userPayload)
      .eq('user_id', user.id)

    if (updateError) {
      return {
        ok: false,
        error: updateError.message || 'Could not save your profile.',
      }
    }
  } else {
    const { error: insertError } = await supabase.from(TABLE_NAMES.USERS).insert([
      {
        user_id: user.id,
        ...userPayload,
        privacy_policy_terms: 'yes',
      },
    ])

    if (insertError) {
      return {
        ok: false,
        error: insertError.message || 'Could not create your profile.',
      }
    }

    try {
      await ensureInitialRatings(user.id)
    } catch (ratingErr) {
      console.error('Initial ratings insert failed', ratingErr)
    }
  }

  await tryWriteSignupExtras(user.id, {
    persona: 'community',
    account_status: 'activated',
    organization_name: trimmedName,
    primary_sports: primarySports,
    signup_step: 2,
  })

  persistCommunityContext({
    communityName: trimmedName,
    managerName: displayName || existing?.name,
    userId: user.id,
  })

  try {
    const draft = readDraft()
    await persistWizardVenuesToCommunityLocations(
      communityId,
      draft.setupWizard?.venues || [],
    )
  } catch (venueErr) {
    console.error('Wizard venues → community_locations failed', venueErr)
  }

  return { ok: true, communityId, communityIdJoin }
}
